import axios, { AxiosInstance, AxiosRequestHeaders } from 'axios';

import breakpoints from './breakpoints';
import { attachSessionRefreshInterceptors } from './sessionRefreshInterceptor';

const { REACT_APP_BLOX_BI_URL } = process.env;

class BloxBiService {
  instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: REACT_APP_BLOX_BI_URL,
      maxRedirects: 0, // Prevent automatic redirects
    });
    this.instance.interceptors.request.use(async (config) => {
      const token = localStorage.getItem('BLOX_USER_TOKEN');

      (config.headers as AxiosRequestHeaders).authorization = `Bearer ${token}` as string;
      (config.headers as AxiosRequestHeaders)['Content-Type'] = 'application/json';
      config.timeout = 60 * 4 * 1000;
      return config;
    });

    attachSessionRefreshInterceptors(this.instance);
  }

  // bi services
  createDashboardChart = ({ request_body }: any) => {
    return this.instance.post(breakpoints.createchart(), request_body);
  };

  updateDashboardChart = ({ request_body }: any) => {
    return this.instance.put(breakpoints.createchart(), request_body);
  };

  getDashboardChart = (chartId: any) => {
    return this.instance.get(breakpoints.getChart(chartId));
  };

  getDashboardGraph = (chartId: any, scenarioId: number, requestBody: any) =>
    this.instance.post(breakpoints.getGraph(chartId, scenarioId), requestBody);

  addChartMetric = ({ request_body }: any) =>
    this.instance.post(breakpoints.addMetric(), request_body);

  updateChartMetric = ({ request_body }: any) =>
    this.instance.put(breakpoints.addMetric(), request_body);

  addChartFilter = ({ request_body }: any) =>
    this.instance.post(breakpoints.chartFilter(), request_body);

  updateConditionalFormatting = ({ request_body }: any) =>
    this.instance.put(breakpoints.conditionalFormatting(), request_body);

  addConditionalFormatting = ({ request_body }: any) =>
    this.instance.post(breakpoints.conditionalFormatting(), request_body);

  getAllCharts = (dashboardId: string) => this.instance.get(breakpoints.allCharts(dashboardId));

  updateChartLayout = ({ request_body }: any) =>
    this.instance.put(breakpoints.updateLayout(), request_body);

  updateInstrumentType = ({ request_body }: any) =>
    this.instance.put(breakpoints.updateInstrumentType(), request_body);

  updateChartFilter = ({ request_body }: any) =>
    this.instance.put(breakpoints.chartFilter(), request_body);

  deleteChartFilter = (filterId: string) =>
    this.instance.delete(breakpoints.deleteFilter(filterId));

  deleteConditionalFormatting = (conditionId: string) =>
    this.instance.delete(breakpoints.deleteConditional(conditionId));

  deleteChart = (chartId: any) => this.instance.delete(breakpoints.deleteChart(chartId));

  deleteChartMetric = (metricId: any) =>
    this.instance.delete(breakpoints.deleteChartMetric(metricId));

  reorderChartMetric = ({ request_body }: any) =>
    this.instance.put(breakpoints.reorderChartMetric(), request_body);

  fetch_dashboardid = (modelId: any) => this.instance.get(breakpoints.dashboard(modelId));

  create_dashboard = ({ request_body }: any) =>
    this.instance.post(breakpoints.createDashboard(), request_body);

  update_dashboard = ({ request_body }: any) =>
    this.instance.put(breakpoints.updateDashboard(), request_body);

  dashboard_time_filter = ({ request_body }: any) =>
    this.instance.post(breakpoints.dashboardTimeFilter(), request_body);

  dashboard_metadata = (dashboardId: string) =>
    this.instance.get(breakpoints.dashboardMetadata(dashboardId));

  /**
   * Full dashboard export using the new anchor/window reporting model.
   *
   * @param dashboardId          - Dashboard UUID.
   * @param startDate            - Display timescale start in MMM-YY.
   * @param endDate              - Display timescale end in MMM-YY.
   * @param scenarioId           - Active scenario ID.
   * @param modelId              - Model ID.
   * @param anchorPeriod         - Reporting anchor in YYYY-MM (e.g. "2026-03").
   * @param reportingWindow      - Window type (SINGLE | YTD | FY | ROLLING_12).
   * @param reportingStart       - Resolved reporting start in MMM-YY.
   * @param reportingEnd         - Resolved reporting end in MMM-YY.
   * @param compareScenarioId    - Optional compare-scenario ID.
   * @param preset               - Legacy preset string (kept for Variance renderer compatibility).
   */
  dashboard_full_export_pdf = (
    dashboardId: string,
    startDate: string,
    endDate: string,
    scenarioId: string,
    modelId: string,
    anchorPeriod: string,
    reportingWindow: string,
    reportingStart: string,
    reportingEnd: string,
    compareScenarioId?: string | number,
    preset?: string,
  ) =>
    this.instance.get(
      breakpoints.dashboardFullExport(
        dashboardId,
        startDate,
        endDate,
        scenarioId,
        modelId,
        anchorPeriod,
        reportingWindow,
        reportingStart,
        reportingEnd,
        compareScenarioId,
        preset,
      ),
    );

  /**
   * Fetches all instrument data values for a dashboard page.
   * Sends both the new anchor/window params and the pre-resolved start/end
   * so the backend can use whichever it prefers.
   *
   * @param chartId              - Page UUID.
   * @param scenarioId           - Active scenario ID.
   * @param startDate            - Display timescale start in MMM-YY.
   * @param endDate              - Display timescale end in MMM-YY.
   * @param compareScenarioId    - Optional compare-scenario ID.
   * @param anchorPeriod         - Reporting anchor in YYYY-MM (e.g. "2026-03").
   * @param reportingWindow      - Window type (SINGLE | YTD | FY | ROLLING_12).
   * @param reportingStart       - Resolved reporting period start in MMM-YY.
   * @param reportingEnd         - Resolved reporting period end in MMM-YY.
   * @param headers              - Optional extra request headers.
   */
  getDashboardDataValuesWithTime = (
    chartId: string,
    scenarioId: string | number,
    startDate: string,
    endDate: string,
    compareScenarioId?: string | number | null,
    anchorPeriod?: string,
    reportingWindow?: string,
    reportingStart?: string,
    reportingEnd?: string,
    headers: Record<string, string> = {},
  ) => {
    const url = breakpoints.dashboardDataValues(chartId, Number(scenarioId));
    const parameters: Record<string, string | number> = {
      start_date: startDate,
      end_date: endDate,
    };

    if (anchorPeriod) parameters.reporting_anchor_period = anchorPeriod;
    if (reportingWindow) parameters.reporting_window = reportingWindow;
    if (reportingStart) parameters.reporting_start_period = reportingStart;
    if (reportingEnd) parameters.reporting_end_period = reportingEnd;
    if (compareScenarioId) parameters.compare_scenario = compareScenarioId;

    return this.instance.get(url, { headers, params: parameters });
  };

  // Page management APIs
  create_page = ({ request_body }: any) => {
    return this.instance.post(breakpoints.createPage(), request_body);
  };

  update_page = ({ request_body }: any) => {
    return this.instance.put(breakpoints.updatePage(), request_body);
  };

  delete_page = (pageId: string) => {
    return this.instance.delete(breakpoints.deletePage(pageId));
  };

  listModelDashboards = (modelId: number | string) =>
    this.instance.get(breakpoints.modelDashboards(modelId));

  createModelDashboard = (modelId: number | string, request_body: Record<string, unknown>) =>
    this.instance.post(breakpoints.modelDashboards(modelId), request_body);

  getDashboardById = (dashboardId: string) =>
    this.instance.get(breakpoints.dashboardById(dashboardId));

  updateDashboardById = (dashboardId: string, request_body: Record<string, unknown>) =>
    this.instance.put(breakpoints.dashboardById(dashboardId), request_body);

  duplicateDashboard = (dashboardId: string, body?: { name?: string }) =>
    this.instance.post(breakpoints.dashboardCopy(dashboardId), body ?? {});

  deleteDashboardById = (dashboardId: string) =>
    this.instance.delete(breakpoints.dashboardById(dashboardId));

  createDashboardSnapshot = (dashboardId: string, body: Record<string, unknown>) =>
    this.instance.post(breakpoints.dashboardSnapshots(dashboardId), body);

  createDashboardSnapshotArtifacts = (snapshotId: string, formats: Array<'pdf' | 'xlsx'>) =>
    this.instance.post(breakpoints.dashboardSnapshotArtifacts(snapshotId), { formats });

  /**
   * Download a rendered artifact. `downloadPath` is the relative path from the artifacts API.
   */
  downloadDashboardSnapshotArtifact = (downloadPath: string) =>
    this.instance.get(downloadPath, { responseType: 'blob' });

  getDashboardPackDataValues = (
    dashboardId: string,
    parameters: Record<string, string | number | boolean | undefined>,
  ) => this.instance.get(breakpoints.dashboardPackDataValues(dashboardId), { params: parameters });
}

export default new BloxBiService();
